// v3/design-system/primitives/input.tsx — Input primitive
// Shadcn/UI pattern: forwardRef, cn() merging, 44px touch target.
// Zero imports from v1/v2.

import * as React from 'react';

import { cn } from '../utils';

/** Input types that are safe to trim leading/trailing whitespace from on blur. */
const TRIM_TYPES = new Set<string | undefined>([
  undefined,
  'text',
  'email',
  'search',
  'tel',
  'url',
]);

/**
 * Push a trimmed value through the native value setter and dispatch an
 * `input` event so React's synthetic `onChange` fires and the controlling
 * parent's state updates. No-ops if the value is already trimmed.
 */
export function commitTrimmed(el: HTMLInputElement | HTMLTextAreaElement) {
  const trimmed = el.value.trim();
  if (trimmed === el.value) return;
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(el, trimmed);
  el.dispatchEvent(new Event('input', { bubbles: true })); // React picks this up as onChange
}

type InputProps = React.ComponentProps<'input'> & {
  /** Opt out of trim-on-blur (default: trims for text-like inputs). */
  noTrim?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, noTrim, onBlur, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex min-h-[44px] h-11 w-full rounded-md border border-input bg-background px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          props['aria-invalid'] && 'border-red-500 focus-visible:ring-red-500',
          className,
        )}
        ref={ref}
        onBlur={(e) => {
          if (!noTrim && TRIM_TYPES.has(type)) commitTrimmed(e.currentTarget);
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
